import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, BookOpen } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { PYQ_EXAMS, getPyqExam } from "@/data/pyqArchive";

const SITE = "https://hireds.in";

export function PyqIndex() {
  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Previous Year Questions (PYQ) Archive — SSC, RRB, UPSC, IBPS | hireds.in";
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", "Solve previous year questions with detailed explanations for SSC CGL, RRB NTPC, UPSC Prelims, IBPS PO and RRB ALP. Free PYQ archive with answer keys.");
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/pyq`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="pyq-index">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "PYQ Archive", url: "/pyq" },
      ]} />
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Free Prep Resource</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Previous Year Questions (PYQ) Archive
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          Hand-picked previous year questions with detailed explanations for India's most-attempted government exams. Solve, reveal answers, and read why the correct option is right.
        </p>
      </header>

      <ul className="grid sm:grid-cols-2 gap-4">
        {PYQ_EXAMS.map((e) => (
          <li key={e.slug}>
            <Link
              to={`/pyq/${e.slug}`}
              data-testid={`pyq-card-${e.slug}`}
              className="block h-full p-5 rounded-xl border border-slate-200 bg-white hover:border-orange-400 hover:shadow-md transition"
            >
              <div className="flex items-start gap-3">
                <span className="inline-grid place-items-center w-9 h-9 rounded-md bg-orange-100 text-orange-700 shrink-0">
                  <BookOpen className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900 leading-snug" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
                    {e.exam} — PYQ Set ({e.year})
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">{e.full_name}</p>
                  <p className="text-sm text-slate-700 mt-2">{e.questions.length} questions · with explanations</p>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PyqDetail() {
  const { examSlug } = useParams();
  const exam = getPyqExam(examSlug);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    if (!exam) return;
    const oldTitle = document.title;
    document.title = `${exam.exam} Previous Year Questions ${exam.year} — Answers & Explanations | hireds.in`;
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", `${exam.questions.length} ${exam.exam} previous year questions from ${exam.year} with step-by-step explanations and official answer keys.`);
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/pyq/${exam.slug}`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, [exam]);

  if (!exam) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 text-center" data-testid="pyq-not-found">
        <h1 className="text-2xl font-extrabold text-slate-900">Exam not found</h1>
        <p className="text-sm text-slate-600 mt-2">
          We have not added this exam yet. Browse the <Link to="/pyq" className="text-orange-600 underline">full PYQ archive</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="pyq-detail">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "PYQ Archive", url: "/pyq" },
        { name: exam.exam, url: `/pyq/${exam.slug}` },
      ]} />
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">{exam.year} · Official Answer Key</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {exam.exam} — Previous Year Questions
        </h1>
        <p className="text-sm text-slate-500 mt-2">{exam.full_name}</p>
        <p className="text-sm text-slate-700 mt-3 leading-relaxed bg-amber-50 border border-amber-200 rounded p-3">{exam.intro}</p>
      </header>

      <ol className="space-y-5">
        {exam.questions.map((q, i) => {
          const isRevealed = revealed[i];
          return (
            <li key={i} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`pyq-q-${i}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className="inline-grid place-items-center w-7 h-7 rounded-md bg-slate-900 text-white text-xs font-extrabold shrink-0">{i + 1}</span>
                <p className="text-base font-semibold text-slate-900 leading-snug">{q.q}</p>
              </div>
              <ul className="grid sm:grid-cols-2 gap-2 mb-3 pl-10">
                {q.opts.map((opt, oi) => {
                  const correct = isRevealed && oi === q.a;
                  return (
                    <li
                      key={oi}
                      className={`flex items-center gap-2 px-3 py-2 rounded border text-sm ${
                        correct
                          ? "border-green-400 bg-green-50 text-green-900 font-semibold"
                          : "border-slate-200 bg-slate-50 text-slate-800"
                      }`}
                    >
                      <span className="text-xs font-bold text-slate-500">{String.fromCharCode(65 + oi)}.</span>
                      <span>{opt}</span>
                      {correct && <CheckCircle2 className="w-4 h-4 text-green-700 ml-auto" aria-hidden="true" />}
                    </li>
                  );
                })}
              </ul>
              {!isRevealed ? (
                <button
                  type="button"
                  onClick={() => setRevealed({ ...revealed, [i]: true })}
                  data-testid={`pyq-reveal-${i}`}
                  className="ml-10 px-3 py-1.5 rounded-md bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 transition"
                >
                  Reveal answer &amp; explanation
                </button>
              ) : (
                <div className="ml-10 mt-1 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-slate-800">
                  <strong className="text-green-800">Correct: {String.fromCharCode(65 + q.a)}.</strong> {q.exp}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <p className="mt-8 text-xs text-slate-500 leading-relaxed">
        Questions are curated from the official {exam.year} answer key released by{" "}
        <a href={exam.portal} target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">
          the official portal
        </a>. Explanations are written by the hireds.in editorial team.
      </p>
    </div>
  );
}
